import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { isPast, isToday, sub } from 'date-fns';
import produce from 'immer';
import { BiSync } from 'react-icons/bi';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { IconButton } from '@mui/material';
import { ptBR } from 'date-fns/locale';
import { IEventAnnotationViewModel } from 'viewModels';
import { Event } from './Event';
import { MakingNote } from './MakingNote';

import GoogleSchedule from '../../assets/images/google_calendar.png';

import { NavHeader } from '../../components';
import { useAuth } from '../../context';
import { IEvent, useEvents } from '../../context/events';
import EventController from '../../controller/EventController';
import { useGoogleEvents } from '../../hooks/useGoogleEvents';
import {
  Calendar,
  ChooseDate,
  Container,
  ContainerCalendar,
  Content,
  Header,
  Hour,
  WrapperButtonsSynchronizeSchedule,
  WrapperEvents,
} from './styles';
import { useSolicitation } from '../../context/solicitation';
import { useAlert } from '../../hooks/useAlert';

type IDays = {
  day: string;
  weekDay: string;
  date: Date;
};

type IEventHover = {
  dayMonth: number;
  hour: number;
  id: string;
};

export function Schedule() {
  const alert = useAlert();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { events, setEvents } = useEvents();
  const { solicitations, demandProposals, demands } = useSolicitation();
  const [eventHover, setEventHover] = useState<IEventHover>({} as IEventHover);
  const [daysMonth, setDaysMonth] = useState<IDays[]>([]);
  const [value, setValue] = useState<Date>(new Date());
  const [annotations, setAnnotations] = useState<IEventAnnotationViewModel[]>([]);
  const [hasHoverEvent, setHasHoverEvent] = useState<boolean[][]>(() => {
    return Array(...Array(24)).map(() => {
      return Array(...Array(31)).map(() => false);
    });
  });

  const eventAnnotations = useMemo<IEvent[][]>(() => {
    return Array(...Array(24)).map(() => {
      return Array(...Array(31)).map(() => ({
        type: 'empty',
        color: 'empty',
        date: new Date(),
        id: uuid(),
      }));
    });
  }, []);

  const {
    isLoggedWithGoogle,
    events: eventsGoogle,
    getEvents: getEventsGoogle,
  } = useGoogleEvents(value);

  const getAnnotations = useCallback(() => {
    EventController.getAnnotations(user?.id as string)
      .then((res) => {
        alert.show({ message: 'Suas anotações foram sincronizadas', type: 'success' });
        setAnnotations(res);
      })
      .catch(() => {
        alert.show({ message: 'Ocorreu um erro ao sincronizar suas anotações', type: 'error' });
      });
  }, [alert, user?.id]);

  useEffect(() => {
    if (isLoggedWithGoogle) getEventsGoogle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    EventController.getAnnotations(user?.id as string).then((res) => {
      setAnnotations(res);
    });
  }, [user.id]);

  function mergeEvents(existingEvents: IEvent[], newEvents: IEvent[]): IEvent[] {
    const map = new Map<string, IEvent>();

    for (const event of existingEvents) {
      map.set(event.id, event);
    }

    for (const newEvent of newEvents) {
      map.set(newEvent.id, newEvent);
    }

    return Array.from(map.values());
  }

  useEffect(() => {
    if (eventsGoogle.length > 0) {
      const data = eventsGoogle
        .filter((e) => !!e?.start?.dateTime)
        .map((e) => {
          const eventDate = new Date(e.start?.dateTime!);
          const isSameDay = isToday(eventDate);
          const isPastDate = isPast(eventDate);

          let colorValue = '';

          if (isSameDay || isPastDate) {
            colorValue = 'todayOrLater'; // Fora do prazo (data é o mesmo dia de hoje ou já passou)
          } else {
            colorValue = 'insideTimeLimit'; // Dentro do prazo (data ainda não passou)
          }

          return {
            id: e.id,
            type: 'google',
            color: colorValue,
            date: sub(new Date(e.start?.dateTime!), { hours: 1 }),
            title: e?.summary,
            conference: e?.conferenceData?.entryPoints?.[0].uri,
          } as IEvent;
        });

      setEvents((state) => mergeEvents(state, data));
    }

    if (solicitations.length > 0) {
      const data = solicitations
        .filter((e) => !!e?.dateLimit && e?.status !== 4)
        .map((e) => {
          const eventDate = new Date(e.dateLimit);
          const isSameDay = isToday(eventDate);
          const isPastDate = isPast(eventDate);

          let colorValue = '';

          if (isSameDay || isPastDate) {
            colorValue = 'todayOrLater'; // Fora do prazo (data é o mesmo dia de hoje ou já passou)
          } else {
            colorValue = 'insideTimeLimit'; // Dentro do prazo (data ainda não passou)
          }

          return {
            id: e.id,
            type: 'solicitation',
            date: sub(new Date(e.dateLimit), { hours: 1 }),
            title: e.correspondence.name,
            conference: e.correspondence.name,
            attachments: e.attachments,
            place: e.address,
            value: e.value,
            note: e.note,
            color: colorValue,
            court: e.court,
            solicitation: e,
          } as unknown as IEvent;
        });
      setEvents((state) => mergeEvents(state, data));
    }

    if (demands.length > 0) {
      const data = demands
        .filter((e) => !!e?.dateLimit && e?.status !== 4)
        .map((e) => {
          const eventDate = new Date(e.dateLimit);
          const isSameDay = isToday(eventDate);
          const isPastDate = isPast(eventDate);

          let colorValue = '';

          if (isSameDay || isPastDate) {
            colorValue = 'todayOrLater'; // Fora do prazo (data é o mesmo dia de hoje ou já passou)
          } else {
            colorValue = 'insideTimeLimit'; // Dentro do prazo (data ainda não passou)
          }

          return {
            id: e.id,
            type: 'demand',
            date: sub(new Date(e.dateLimit), { hours: 1 }),
            title: e.correspondence.name,
            conference: e.correspondence.name,
            attachments: e.attachments,
            place: e.address,
            value: e.value,
            note: e.note,
            color: colorValue,
            demand: e,
          } as unknown as IEvent;
        });

      setEvents((state) => mergeEvents(state, data));
    }

    if (demandProposals.length > 0) {
      const data = demandProposals
        .filter((e) => !!e?.dateLimit && e?.status !== 4)
        .map((e) => {
          return {
            id: e.id,
            type: 'demandProposal',
            date: sub(new Date(e.dateLimit), { hours: 1 }),
            title: e.correspondence.name,
            conference: e.correspondence.name,
            place: e.address,
            value: e.value,
            note: e.note,
            color: 'demandProposal',
            demandProposal: e,
          } as unknown as IEvent;
        });

      setEvents((state) => mergeEvents(state, data));
    }

    if (annotations.length > 0) {
      const data = annotations
        .filter((e) => !!e?.date)
        .map(
          (e) =>
            ({
              id: e.id,
              type: 'annotation',
              date: sub(new Date(e.date), { hours: 1 }),
              annotation: e.annotation,
            } as IEvent),
        );

      setEvents((state) => mergeEvents(state, data));
    }
  }, [eventsGoogle, solicitations, annotations, demandProposals, demands, setEvents]);

  const getDaysMonth = (month: number, year: number) => {
    const datesMonth = [];
    const quantityDaysMonth = new Date(year, month + 1, 0).getDate();
    const daysFormatted: IDays[] = [];

    for (let i = 1; i <= quantityDaysMonth; i++) {
      const day = new Date(year, month, i);
      datesMonth.push(day);
    }
    datesMonth.forEach((date) => {
      const weekDay = date.toLocaleString('pt-br', { weekday: 'long' }).split('-')[0];
      const day = date?.getDate();

      daysFormatted.push({
        weekDay: weekDay.charAt(0).toUpperCase() + weekDay.slice(1),
        day: day?.toString(),
        date,
      });
    });

    setDaysMonth(daysFormatted);
  };

  useEffect(() => {
    const year = new Date()?.getFullYear();
    const month = new Date()?.getMonth();
    getDaysMonth(month!, year!);
  }, []);

  const handleOnMouseOverEvent = (row: number, column: number) => {
    setHasHoverEvent(
      produce((draft) => {
        /*eslint-disable-next-line*/
        draft[row][column] = true;
      }),
    );
  };
  const handleOnMouseOutEvent = (row: number, column: number) => {
    setEventHover({} as IEventHover);

    setHasHoverEvent(
      produce((draft) => {
        /*eslint-disable-next-line*/
        draft[row][column] = false;
      }),
    );
  };

  const calendarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const hour = new Date().getHours();
    const day = new Date().getDate();
    calendarRef.current?.scrollTo({ top: hour * 90 - 90, left: day * 120 });
  }, [daysMonth]);

  function handleRemoveEvent(id: string): () => void {
    return () => {
      setEvents((state) => state.filter((e) => e.id !== id));
    };
  }

  return (
    <Container>
      <NavHeader typeHeader="company" />
      <Content>
        {/* <WrapperButtonsSynchronizeSchedule>
          <div>
            <BiSync size={26} color={colors.tertiary_500} />
            <p>Sincronizar</p>
          </div>
          <IconButton onClick={getEventsGoogle}>
            <img src={GoogleSchedule} alt="icon-google-schedule" className="icon-google-schedule" />
          </IconButton>
        </WrapperButtonsSynchronizeSchedule> */}
        <Header>
          <div>
            <h1>Agenda</h1>
          </div>
        </Header>
        <ChooseDate>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              views={['year', 'month']}
              minDate={new Date(1970, 0, 0)}
              value={value}
              onChange={(dateSelected) => {
                const year = dateSelected?.getFullYear();
                const month = (dateSelected?.getMonth() as number) + 1;

                if (dateSelected) {
                  setValue(dateSelected);
                  getDaysMonth(month!, year!);
                }
              }}
              //renderInput={(params) => <TextField {...params} helperText={null} />}
              // InputProps={{
              //   onKeyDown: (e) => e.preventDefault(),
              // }}
              // PaperProps={{
              //   style: {
              //     color: colors.text_gray,
              //   },
              // }}
            />
          </LocalizationProvider>
        </ChooseDate>
        <ContainerCalendar ref={calendarRef}>
          <Calendar>
            <thead>
              <tr>
                <th />
                {daysMonth.map((item) => (
                  <th key={item.day} className="day-week">
                    <strong>{item.day.padStart(2, '0')}</strong>
                    <p>{item.weekDay}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array(...Array(24)).map((_, hour) => {
                const color = (hour + 1) % 2 === 0 ? colors.background : colors.background_white;
                return (
                  <tr key={hour}>
                    <Hour color={color}>{hour + 1}</Hour>
                    {Array(...Array(daysMonth?.length || 0)).map((_, dayMonth) => {
                      const filteredEvents: IEvent[] = events.filter(
                        (e) =>
                          e?.date?.getDate() === dayMonth + 1 &&
                          e?.date?.getMonth() === value?.getMonth() &&
                          e?.date?.getHours() === hour,
                      );

                      if (filteredEvents.length > 0) {
                        filteredEvents.push(eventAnnotations[hour][dayMonth]);
                      }

                      return filteredEvents.length > 0 ? (
                        <WrapperEvents
                          key={hour + dayMonth.toString()}
                          style={{
                            zIndex: filteredEvents.some((f) => f.id === eventHover.id)
                              ? filteredEvents.length
                              : 1,
                          }}
                          color={
                            (hour + 1) % 2 === 0 ? colors.background : colors.background_white
                          }>
                          {filteredEvents.map((item, indexFiltered) => {
                            return (
                              <Event
                                key={item.id}
                                hasBlur={
                                  !!(
                                    filteredEvents.some((f) => f.id === eventHover.id) &&
                                    eventHover.id &&
                                    item.id !== eventHover.id
                                  )
                                }
                                onMouseLeave={() => {
                                  handleOnMouseOutEvent(hour, dayMonth);
                                }}
                                onMouseEnter={() => {
                                  handleOnMouseOverEvent(hour, dayMonth);

                                  setEventHover({
                                    id: item.id,
                                    dayMonth,
                                    hour,
                                  });
                                }}
                                zIndex={filteredEvents.length - indexFiltered - 5}
                                left={
                                  hasHoverEvent[hour][dayMonth]
                                    ? indexFiltered * 80
                                    : indexFiltered * 15
                                }
                                type={item.type}
                                color={item.color}
                                data={item}
                                date={
                                  new Date(value.getFullYear(), value.getMonth(), dayMonth, hour)
                                }
                                getAnnotations={getAnnotations}
                                removeEvent={
                                  item.type === 'demandProposal'
                                    ? handleRemoveEvent(item.id)
                                    : undefined
                                }
                              />
                            );
                          })}
                        </WrapperEvents>
                      ) : (
                        <MakingNote
                          index={hour}
                          day={dayMonth + 1}
                          month={value.getMonth()}
                          year={value.getFullYear()}
                          hour={hour + 1}
                          key={hour + dayMonth.toString()}
                          getAnnotations={getAnnotations}
                        />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Calendar>
        </ContainerCalendar>
      </Content>
    </Container>
  );
}
